<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex d-flex xs12 sm4>
        <v-radio-group v-model="type" row>
          <v-radio :label="$tc('tournament')" value="tournament" color="primary"></v-radio>
          <v-radio :label="$tc('phase')" value="phase" color="primary"></v-radio>
          <v-radio :label="$tc('match')" value="match" color="primary"></v-radio>
          <v-radio :label="$tc('act')" value="act" color="primary"></v-radio>
        </v-radio-group>
      </v-flex>
      <v-flex v-if="type == 'phase'" d-flex xs12 md3>
        <v-autocomplete
          :items="listPhases"
          :label="$t('select', {field: $tc('phase')})"
          v-model="phaseId"
        ></v-autocomplete>
      </v-flex>
      <v-flex v-if="type == 'match'" d-flex xs12 md3>
        <v-autocomplete
          :items="listMatches"
          :label="$t('select', {field: $tc('match')})"
          v-model="matchId"
        >
          <template v-slot:selection="data">{{ data.item.text }}</template>
          <template v-slot:item="data">
            <v-list-tile-content>
              <v-list-tile-title>{{data.item.text}}</v-list-tile-title>
              <v-list-tile-sub-title>{{`${data.item.phase} - ${data.item.date}`}}</v-list-tile-sub-title>
            </v-list-tile-content>
          </template>
        </v-autocomplete>
      </v-flex>
      <template v-if="type != 'act'">
        <v-flex v-if="tournamentType == 'teams'" d-flex xs12 md3 px-2>
          <v-autocomplete
            :items="listTeams"
            :label="$t('select', {field: $tc('team')})"
            v-model="teamId"
          ></v-autocomplete>
        </v-flex>
        <v-flex
          v-if="tournamentType =='users' || tournamentType == 'usersTeams'"
          d-flex
          xs12
          md3
          px-2
        >
          <v-autocomplete
            :items="listUsers"
            :label="$t('select', {field: $tc('user')})"
            v-model="userId"
          ></v-autocomplete>
        </v-flex>
      </template>
      <v-flex align-self-center px-2>
        <v-btn @click="getStats" class="ma-0" color="primary" small>
          <span v-if="loading">
            <v-progress-circular :size="20" :width="3" color="#FFFFFF" indeterminate></v-progress-circular>
          </span>
          <span v-else>{{$t('search')}}</span>
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout row wrap>
      <v-flex v-if="averagesPhases" d-flex xs12 pa-2>
        <stacked-line-chart
          :title="averagesPhases.title"
          :legend="averagesPhases.categories"
          :x-axis-label="averagesPhases.phases"
          :series="averagesPhases.series"
        />
      </v-flex>
      <v-flex v-if="averageStats && !matchesStats.length" d-flex xs12 pa-2>
        <bar-chart :title="averageStats.title" :data="averageStats.stats" :twoBars="false"></bar-chart>
      </v-flex>
      <v-flex d-flex xs12 pa-2 v-for="(stat, index) of matchesStats" :key="index + 1100">
        <bar-chart :title="stat.title" :data="stat.stats"></bar-chart>
      </v-flex>

      <v-flex v-if="stanceResult.length" d-flex xs12 sm6 pa-2>
        <donut-chart :title="$t('stanceResult')" :data="stanceResult"></donut-chart>
      </v-flex>
      <v-flex v-if="wonLostResult.length" d-flex xs12 sm6 pa-2>
        <donut-chart :title="$t('wonLostResult')" :data="wonLostResult"></donut-chart>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import BarChart from "@/components/widgets/chart/BarChart";
import DonutChart from "@/components/widgets/chart/DonutChart";
import StackedLineChart from "@/components/widgets/chart/StackedLineChart";
import { dateFormat } from "@/helpers/dateHelper";
import queueToast from "@/helpers/queueToast";

export default {
  name: "tabStats",
  components: { BarChart, DonutChart, StackedLineChart },
  props: {
    tournamentId: {
      type: String,
      default: ""
    },
    tournamentType: {
      type: String,
      default: ""
    },
    phases: {
      type: Array,
      default: () => []
    },
    actId: {
      type: String,
      default: ""
    },
    users: {
      type: Array,
      default: () => []
    },
    teams: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      type: "tournament",
      phaseId: "",
      matchId: "",
      userId: "",
      teamId: ""
    };
  },

  watch: {
    type(val) {
      this.userId = "";
      this.teamId = "";
      this.matchId = "";
      this.phaseId = "";
    },
    tournament(val) {
      this.setClear();
    }
  },
  computed: {
    ...mapState({
      totalMatches: state => state.stats.totalMatches,
      loading: state => state.stats.loading,
      actSelectList: state => state.acts.listSelect,
      listSelectUsers: state => state.users.listSelectUsers,
      listSelectTeams: state => state.teams.listSelectTeams,
      tournament: state => state.tournaments.selected,
      authUser: state => state.authentication.user
    }),
    ...mapGetters({
      averageStats: "stats/averageStats",
      matchesStats: "stats/matchesStats",
      stanceResult: "stats/stanceResult",
      wonLostResult: "stats/wonLostResult",
      averagesPhases: "stats/averagesPhases"
    }),
    listPhases() {
      return this.phases.map(phase => ({
        value: phase._id,
        text: this.getName(phase.name)
      }));
    },
    listMatches() {
      if (!this.tournament) return [];
      let matchList = this.tournament.matches || [];

      matchList.forEach(match => {
        let phase = this.tournament.phases.find(phase =>
          phase.groups.some(group => group.matches.includes(match._id))
        );
        if (phase) {
          match.phase = this.getName(phase.name);
        }
      });
      let type = this.tournamentType == "teams" ? "team" : "user";
      return this.formatMatches(matchList, type);
    },

    listUsers() {
      if (!this.users) return [];
      let users = this.users.map(user => ({
        value: user.userId,
        text: `${user.name} (${user.entity})`
      }));

      if (this.matchId && this.tournamentType == "users") {
        if (!this.listMatches) return [];
        let match = this.listMatches.find(match => match.value == this.matchId);
        if (match && match.user) {
          users = users.filter(user => user.value == match.user);
        }
      }
      if (this.isParticipant) {
        users = users.filter(user => user.value == this.authUser._id);
      } else {
        users = users.filter(user =>
          this.listSelectUsers.some(item => item.value == user.value)
        );
      }
      return users;
    },
    listTeams() {
      let teams = this.teams.map(team => ({
        value: team.teamId,
        text: `${team.name} (${team.entity})`,
        name: team.name
      }));
      if (this.matchId) {
        let match = this.listMatches.find(match => match.value == this.matchId);

        teams = teams.filter(
          team =>
            team.value == match.homeTeamId || team.value == match.visitorTeamId
        );
      }
      return teams.filter(team =>
        this.listSelectTeams.some(item => item.value == team.value)
      );
    }
  },
  methods: {
    ...mapActions({
      setTotalMatches: "stats/setTotalMatches",
      setAveragesPhases: "stats/setAveragesPhases",
      setAverages: "stats/setAverages",
      setClear: "stats/setClear"
    }),
    getStats() {
      if (!this.validate()) return;
      let params = {};
      switch (this.type) {
        case "phase":
          params.phaseId = this.phaseId;
        case "tournament":
          params.tournamentId = this.tournamentId;
          break;
        case "match":
          params.matchId = this.matchId;
          break;
        case "act":
          params.actId = this.actId;
          break;
      }
      this.setClear();
      if (this.type != "act") {
        if (this.userId) params.userId = this.userId;
        else if (this.teamId) params.teamId = this.teamId;
      }
      if (params.tournamentId && params.teamId) {
        this.setTotalMatches(params);
      }
      if (this.type == "tournament") {
        this.setAveragesPhases(params);
      }

      this.setAverages(params);
    },

    formatMatches(matches, type) {
      if (type == "user" && this.users) {
        return matches.map(match => {
          let user = this.users.find(user => user.userId == match.user);
          let date = dateFormat(match.date, false);
          return {
            value: match._id,
            text: user ? `${user.name} (${user.entity})` : "*",
            date: date + " - " + match.time,
            phase: match.phase,
            user: match.user
          };
        });
      } else if (type == "team" && this.teams) {
        return matches.map(match => {
          let homeTeam = this.teams.find(
            team => team.teamId == match.homeTeamId
          );
          let visitorTeam = this.teams.find(
            team => team.teamId == match.visitorTeamId
          );
          let date = dateFormat(match.date, false);
          homeTeam = homeTeam ? homeTeam.name : "*";
          visitorTeam = visitorTeam ? visitorTeam.name : "*";
          return {
            value: match._id,
            text: homeTeam + " vs " + visitorTeam,
            date: date + " - " + match.time,
            phase: match.phase,
            homeTeamId: match.homeTeamId,
            visitorTeamId: match.visitorTeamId
          };
        });
      }
    },
    validate() {
      let error = false;
      let messages = [];
      if (this.type == "match" && !this.matchId) {
        error = true;
        messages.push(this.$t("error.required", { field: this.$tc("match") }));
      }
      if (this.type == "phase" && !this.phaseId) {
        error = true;
        messages.push(this.$t("error.required", { field: this.$tc("phase") }));
      }

      if (error) {
        queueToast(messages);
        return;
      }
      return !error;
    }
  }
};
</script>

<style scoped lang="scss">
.v-content {
}
</style>
